import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Calendar, Target, Users, FileText, Eye, Download } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import adminAuditService from '../../../../../../services/audit_services/adminAuditService';
import exportAuditProgramService from '../../../../../../services/audit_services/export_pdf_services/exportAuditProgramService';

export default function AdminAuditProgramDetails() {
  const { id } = useParams();
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchProgramDetails();
  }, [id]);

  const fetchProgramDetails = async () => {
    setLoading(true);
    
    const result = await adminAuditService.getAuditProgramDetail(authTokens.access, id);
    
    if (result.success) {
      setProgram(result.data);
    } else {
      showError(result.error || 'Erreur lors du chargement des détails du programme');
    }
    
    setLoading(false);
  };

  const handleExportPDF = async () => {
    setExporting(true);
    const result = await exportAuditProgramService.exportAuditProgramPDF(authTokens.access, id);
    
    if (result.success) {
      showSuccess('Programme d\'audit exporté avec succès');
    } else {
      showError(result.error || 'Erreur lors de l\'exportation du programme');
    }
    
    setExporting(false);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      opened: { bg: 'bg-green-100', text: 'text-green-800', label: 'Ouvert' },
      closed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Fermé' },
      achieved: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Archivé' }
    };

    const config = statusConfig[status] || statusConfig.opened;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      auditor: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Auditeur' },
      auditee: { bg: 'bg-green-100', text: 'text-green-800', label: 'Audité' },
      participant: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Participant' },
      approver: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Approbateur' }
    };

    const config = roleConfig[role] || roleConfig.participant;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails du programme...</p>
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Programme non trouvé</p>
        <Link to="/dashboard/admin/audits/programs" className="text-green-600 hover:underline mt-4 inline-block">
          Retour aux programmes
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard/admin/audits/programs"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{program.title}</h1>
            <p className="text-sm text-gray-600 mt-1">Détails du programme d'audit</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {getStatusBadge(program.status)}
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Export en cours...' : 'Exporter PDF'}
          </button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Program Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations du programme</h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-green-600" />
                  Date de création
                </p>
                <p className="text-sm text-gray-900">
                  {new Date(program.created_at).toLocaleDateString('fr-FR', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Target className="h-4 w-4 mr-2 text-green-600" />
                  Objectif
                </p>
                <p className="text-sm text-gray-900">{program.objective}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Périmètre</p>
                <ul className="text-sm text-gray-900 space-y-1">
                  {program.scope && program.scope.length > 0 ? (
                    program.scope.map((scopeItem, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-green-600 mr-2">•</span>
                        {scopeItem}
                      </li>
                    ))
                  ) : (
                    <li className="text-gray-500">Aucun périmètre défini</li>
                  )}
                </ul>
              </div>

              {program.approvers && program.approvers.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Approbateurs</p>
                  <div className="space-y-2">
                    {program.approvers.map((approver) => (
                      <div key={approver.id} className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-green-600">
                            {approver.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{approver.name}</p>
                          <p className="text-xs text-gray-500">{approver.email}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Parties Involved */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center mb-4">
              <Users className="h-4 w-4 mr-2 text-green-600" />
              Parties impliquées ({program.parties ? program.parties.length : 0})
            </h3>
            <div className="space-y-3">
              {program.parties && program.parties.length > 0 ? (
                program.parties.map((party) => (
                  <div key={party.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-600">
                          {party.user_name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{party.user_name}</p>
                        <p className="text-xs text-gray-500">{party.user_email}</p>
                      </div>
                    </div>
                    <div>
                      {getRoleBadge(party.role)}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">Aucune partie assignée</p>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Métadonnées</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-600">Créé par</p>
                <p className="text-sm text-gray-900 mt-1">{program.created_by?.full_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Date de création</p>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(program.created_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Dernière mise à jour</p>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(program.updated_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
              {program.last_updated_by && (
                <div>
                  <p className="text-xs text-gray-600">Dernière modification par</p>
                  <p className="text-sm text-gray-900 mt-1">{program.last_updated_by.full_name}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* View Audit Plans Link */}
      {/* Removed - Organization admin has read-only access */}
    </div>
  );
}
